export type ArticleCategory =
  | "After a Bite"
  | "Identification"
  | "Alpha-Gal"
  | "Diseases"
  | "Prevention"
  | "Pets & Yard"
  | "Outdoor Activities"
  | "Maps & Seasons";

export interface ArticleSection {
  h: string;
  points: string[];
}

export interface Article {
  slug: string;
  title: string;
  /** The <title>, when the heading is too long for a search result. */
  metaTitle?: string;
  description: string;
  category: ArticleCategory;
  readMinutes: number;
  updated: string; // ISO yyyy-mm-dd
  keywords: string[];
  intro: string;
  sections: ArticleSection[];
  takeaways: string[];
}

export const ARTICLES: Article[] = [
  {
    slug: "how-to-safely-remove-a-tick",
    title: "How to Safely Remove a Tick (Without Making It Worse)",
    description:
      "Step-by-step instructions for removing an attached tick with fine-tipped tweezers, plus what to avoid and how to care for the bite area.",
    category: "After a Bite",
    readMinutes: 4,
    updated: "2026-06-01",
    keywords: ["how to remove a tick", "tick removal", "remove tick from skin"],
    intro:
      "Proper tick removal is simple, but small mistakes (twisting, burning, or smothering) can leave mouthparts behind or stress the tick into releasing more fluid. Here's the right way.",
    sections: [
      {
        h: "Tools to keep on hand",
        points: [
          "Fine-tipped tweezers (not standard household tweezers).",
          "Rubbing alcohol or soap and water.",
          "Sealed plastic bag and a marker for labeling the tick.",
          "Magnifying glass or phone camera with zoom for tiny nymphs.",
        ],
      },
      {
        h: "Removal steps",
        points: [
          "Grip the tick by the mouthparts, as close to the skin as possible.",
          "Pull straight up with steady, even pressure. Never twist or jerk.",
          "If mouthparts break off, leave them; the body will usually expel them.",
          "Clean the bite area with alcohol or soap and water.",
        ],
      },
      {
        h: "After removal",
        points: [
          "Save the tick in a bag labeled with the date, body location, and where you were.",
          "Photograph the tick next to a coin for scale.",
          "Mark a small reminder in your calendar 30 days out to check for symptoms.",
        ],
      },
    ],
    takeaways: [
      "Fine-tipped tweezers + straight pull is the safest method.",
      "Skip the folk remedies. They can make things worse.",
      "Document the bite in case symptoms appear later.",
    ],
  },
  {
    slug: "permethrin-clothing-guide",
    title: "Permethrin Clothing Treatment: A Beginner's Guide",
    description:
      "Permethrin is one of the most effective tick deterrents. It is applied to clothing, not skin. Here's how it works, how to apply it, and safety basics.",
    category: "Prevention",
    readMinutes: 6,
    updated: "2026-06-01",
    keywords: ["permethrin clothing", "permethrin spray ticks", "how to use permethrin"],
    intro:
      "Permethrin is an insecticide applied to clothing and gear (not skin) that kills or repels ticks on contact. Used correctly, it dramatically reduces the chance of a tick latching on during outdoor activities.",
    sections: [
      {
        h: "How it works",
        points: [
          "Permethrin binds to fabric fibers, lasting through multiple washes.",
          "Kills or disables ticks that crawl onto treated clothing.",
          "Most effective on pants, socks, shoes, and gear like backpacks.",
        ],
      },
      {
        h: "How to apply",
        points: [
          "Hang clothing outside in a well-ventilated area.",
          "Spray evenly until fabric is damp but not soaked.",
          "Let dry completely (2+ hours) before wearing.",
          "Follow label directions for re-treatment intervals (often 6 washes).",
        ],
      },
      {
        h: "Safety notes",
        points: [
          "Never apply to skin. Use EPA-registered skin repellents like DEET or picaridin instead.",
          "Highly toxic to cats while wet; keep cats away during application and drying.",
          "Always follow the EPA-approved product label.",
        ],
      },
    ],
    takeaways: [
      "Apply to clothing only, let dry fully, and keep cats away during application.",
      "Pair with a skin repellent for layered protection.",
      "One treated outfit can cut tick exposure significantly during peak season.",
    ],
  },
  {
    slug: "best-epa-registered-tick-repellents",
    title: "Best EPA-Registered Tick Repellents: DEET, Picaridin, and OLE",
    metaTitle: "EPA-Registered Tick Repellents: DEET, Picaridin, and OLE",
    description:
      "Compare the most effective EPA-registered tick repellents (DEET, picaridin, and oil of lemon eucalyptus), including how to choose for kids and pets.",
    category: "Prevention",
    readMinutes: 7,
    updated: "2026-06-01",
    keywords: ["best tick repellent", "deet vs picaridin", "tick repellent for kids"],
    intro:
      "The EPA registers repellents proven to deter biting insects, including ticks. The three most common active ingredients each have strengths, trade-offs, and use cases. This guide is general education only. Check the product label and talk to your doctor or pediatrician for specifics.",
    sections: [
      {
        h: "DEET",
        points: [
          "Long history of use, effective against ticks at 20–30% concentration.",
          "Lasts several hours; higher concentrations last longer but plateau in effectiveness.",
          "Can damage some plastics and synthetic fabrics.",
        ],
      },
      {
        h: "Picaridin",
        points: [
          "Effective and odorless compared to DEET.",
          "Won't damage gear or fabrics.",
          "Often available as a 20% formulation.",
        ],
      },
      {
        h: "Oil of Lemon Eucalyptus (OLE) / PMD",
        points: [
          "Plant-derived option registered by the EPA.",
          "Not recommended for children under 3 (check label).",
          "Different from pure essential oils, which are not EPA-registered.",
        ],
      },
      {
        h: "How to use repellents safely",
        points: [
          "Apply to exposed skin and clothing, not under clothes.",
          "Avoid eyes, mouth, and cuts.",
          "For kids, spray on adult hands first, then apply.",
          "Always follow label instructions, including age restrictions.",
        ],
      },
    ],
    takeaways: [
      "Look for EPA-registered repellents. Labels list active ingredient and concentration.",
      "DEET, picaridin, and OLE/PMD all repel ticks; choose based on duration and personal preference.",
      "Pair skin repellent with permethrin-treated clothing for the strongest protection.",
    ],
  },
  {
    slug: "family-camping-tick-checklist",
    title: "Family Camping Tick Safety Checklist",
    description:
      "A practical family camping checklist for tick prevention: what to pack, daily routines at camp, and how to check kids for ticks.",
    category: "Outdoor Activities",
    readMinutes: 5,
    updated: "2026-06-01",
    keywords: ["camping with kids ticks", "family tick prevention", "tick safety for kids"],
    intro:
      "Camping with kids is a high-reward, slightly higher-risk tick situation. Build a simple routine: dress for protection, repel correctly, and check often. Then your family vacation stays focused on the campfire.",
    sections: [
      {
        h: "Packing list",
        points: [
          "Permethrin-treated long pants and socks for each person.",
          "Age-appropriate EPA-registered repellent (check label for children's age limits).",
          "Fine-tipped tweezers, alcohol wipes, sealed bags, marker.",
          "Headlamp for evening tick checks.",
        ],
      },
      {
        h: "Daily camp routine",
        points: [
          "Repellent applied each morning and re-applied per label.",
          "Walk in the center of trails; avoid sitting directly on leaf litter.",
          "Mid-day quick check; full check before bed.",
          "Designated 'tick-check tent' or campsite ritual makes it fun for kids.",
        ],
      },
      {
        h: "Where to check kids",
        points: [
          "Hairline and scalp.",
          "Behind ears and around the neck.",
          "Belly button and waistband.",
          "Behind knees, between toes.",
        ],
      },
    ],
    takeaways: [
      "Permethrin-treated clothing dramatically reduces kid exposure.",
      "Make tick checks a 60-second ritual, not a panic event.",
      "Always follow label age limits on repellents.",
    ],
  },
  {
    slug: "ticks-on-the-golf-course",
    title: "Why Golfers Should Care About Lone Star Ticks",
    description:
      "Roughs and tree lines on golf courses are prime tick habitat. Here's a simple round-of-golf prevention routine that takes 60 seconds.",
    category: "Outdoor Activities",
    readMinutes: 4,
    updated: "2026-06-01",
    keywords: ["ticks on golf course", "tick prevention golfers"],
    intro:
      "Most golfers never think about ticks. But every time you search a rough or tree line, you're walking through prime habitat. A quick routine before and after a round prevents most exposures.",
    sections: [
      {
        h: "Before your round",
        points: [
          "EPA-registered repellent on exposed skin (ankles, neck, arms).",
          "Consider permethrin-treated golf pants or socks during peak season.",
          "Keep a small tick removal kit in your bag.",
        ],
      },
      {
        h: "On the course",
        points: [
          "Don't dig deep in heavy brush for a $2 ball.",
          "If you walk into rough or trees, do a quick leg check before the next tee.",
        ],
      },
      {
        h: "After the round",
        points: [
          "Full-body tick check in the locker room or at home.",
          "Hot dryer for golf clothes if you played in heavy brush.",
        ],
      },
    ],
    takeaways: [
      "Tree lines and roughs are tick habitat, even on well-maintained courses.",
      "60 seconds of prevention beats a month of post-bite worry.",
      "Repellent + quick leg checks cover most exposures.",
    ],
  },
  {
    slug: "living-with-alpha-gal-hidden-triggers",
    title: "Living With Alpha-Gal: A Beginner's Guide to Hidden Triggers",
    description:
      "An educational overview of foods, medications, and household products that may contain alpha-gal, plus questions to ask your allergist.",
    category: "Alpha-Gal",
    readMinutes: 7,
    updated: "2026-06-01",
    keywords: ["alpha gal diet", "alpha gal hidden triggers", "what to eat with alpha gal"],
    intro:
      "Alpha-gal syndrome is highly individual. Some people tolerate dairy, some don't; some react to gelatin, some don't. This guide is an educational starting point. Always work with a licensed allergist to build a plan that fits your situation.",
    sections: [
      {
        h: "Common food triggers to discuss with your doctor",
        points: [
          "Beef, pork, lamb, goat, venison, bison, rabbit.",
          "Gelatin (gummy candies, marshmallows, some yogurts).",
          "Broths, gravies, lard, tallow.",
          "Dairy products, though this varies by individual.",
          "Organ meats often contain higher alpha-gal levels.",
        ],
      },
      {
        h: "Non-food triggers to be aware of",
        points: [
          "Some medications and capsules use gelatin or mammalian-derived ingredients.",
          "Certain vaccines and biologics. Always tell providers about AGS.",
          "Some personal care products with collagen or tallow.",
        ],
      },
      {
        h: "Questions for your allergist",
        points: [
          "What specific IgE testing is appropriate?",
          "Should I carry epinephrine?",
          "How should I read ingredient labels?",
          "How might future tick bites affect my sensitivity?",
        ],
      },
    ],
    takeaways: [
      "Alpha-gal is highly individual. Don't apply someone else's diet to yours.",
      "Tell every healthcare provider you have AGS, especially before procedures.",
      "Avoiding additional tick bites is part of long-term management.",
    ],
  },
];

/** Topic order on /resources: what a reader needs first comes first. */
export const ARTICLE_CATEGORIES: ArticleCategory[] = [
  "After a Bite",
  "Identification",
  "Alpha-Gal",
  "Diseases",
  "Prevention",
  "Pets & Yard",
  "Outdoor Activities",
  "Maps & Seasons",
];

export const getArticle = (slug: string) => ARTICLES.find((a) => a.slug === slug);
